import React, { useState, useEffect } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { HeroSection } from '../components/HeroSection';
import { PricingSection } from '../components/PricingSection';
import { BookingSection } from '../components/BookingSection';
import { SimulatorCTA } from '../components/SimulatorCTA';
import { Toaster } from '../components/ui/sonner';

const IntroSplash = ({ onComplete }) => {
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!started) return;
    const audio = new Audio('/assets/car-rev.mp3');
    audio.volume = 0.6;
    audio.play().catch(() => {});
    const timer = setTimeout(() => onComplete(), 3300);
    return () => { clearTimeout(timer); audio.pause(); };
  }, [started, onComplete]);

  if (!started) {
    return (
      <div
        className="fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center cursor-pointer"
        onClick={() => setStarted(true)}
        data-testid="splash-tap-to-enter"
      >
        <div
          className="w-72 sm:w-80 md:w-96 mb-6"
          style={{
            maskImage: 'radial-gradient(ellipse 80% 65% at 50% 45%, black 35%, transparent 80%)',
            WebkitMaskImage: 'radial-gradient(ellipse 80% 65% at 50% 45%, black 35%, transparent 80%)',
          }}
        >
          <img src="/assets/pdx-logo-main.jpeg" alt="PDX Window Tint" className="w-full h-auto" />
        </div>
        <p className="text-zinc-500 text-sm tracking-[0.2em] uppercase animate-pulse">
          Tap to Enter
        </p>
      </div>
    );
  }

  return (
    <div
      className="splash-container fixed inset-0 z-[9999] bg-black flex flex-col items-center justify-center"
      style={{ pointerEvents: 'none' }}
    >
      <div
        className="splash-logo w-72 sm:w-80 md:w-96"
        style={{
          maskImage: 'radial-gradient(ellipse 80% 65% at 50% 45%, black 35%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse 80% 65% at 50% 45%, black 35%, transparent 80%)',
        }}
      >
        <img src="/assets/pdx-logo-main.jpeg" alt="PDX Window Tint" className="w-full h-auto" />
      </div>

      <div className="relative mt-3 mb-2 flex items-center justify-center" style={{ 
        width: '200px', 
        height: '12px',
        opacity: 0,
        animation: 'splashLineIn 0.6s ease-out 0.8s forwards'
      }}>
        <div className="absolute rounded-full" style={{ 
          width: '100%', 
          height: '1px', 
          background: 'linear-gradient(90deg, transparent 0%, #ff2222 20%, #ff4444 50%, #ff2222 80%, transparent 100%)',
          boxShadow: '0 0 4px 1px rgba(255,30,30,0.6), 0 0 12px 2px rgba(255,30,30,0.25)' 
        }} />
        <div className="absolute rounded-full" style={{ 
          width: '16px', 
          height: '3px', 
          background: 'white',
          boxShadow: '0 0 6px 2px rgba(255,255,255,0.9), 0 0 12px 3px rgba(255,100,100,0.4)', 
          top: '50%', 
          transform: 'translateY(-50%)',
          animation: 'laserSweep 2s ease-in-out 1.2s infinite'
        }} />
      </div>

      <p className="splash-tagline text-amber-500/80 text-sm md:text-base font-light tracking-[0.25em] uppercase">
        Tint. Style. Elevate.
      </p>
    </div>
  );
};

const Home = () => {
  const [showBooking, setShowBooking] = useState(false);
  const [activePricingCategory, setActivePricingCategory] = useState(null);
  const [preselectedService, setPreselectedService] = useState(null);
  const [showSplash, setShowSplash] = useState(() => {
    if (sessionStorage.getItem('pdx_intro_seen')) return false;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment_success') === 'true' || params.get('payment_cancelled') === 'true') return false;
    if (params.get('payment') === 'bnpl_success' || params.get('payment') === 'bnpl_cancelled') return false;
    if (params.get('openCategory') || params.get('openBooking')) return false;
    return true;
  });

  // Auto-show booking section on return from payment / deep-link
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // Legacy Square return
    if (params.get('payment_success') === 'true' || params.get('payment_cancelled') === 'true') {
      const saved = localStorage.getItem('pdx_booking_form');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setPreselectedService({ service: parsed.service, package: parsed.package });
          setShowBooking(true);
        } catch (e) {
          console.error('Failed to parse saved booking data', e);
        }
      }
    }
    // BNPL (Stripe Finance) return
    if (params.get('payment') === 'bnpl_success') {
      const saved = localStorage.getItem('pdx_booking_form');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          setPreselectedService({ service: parsed.service, package: parsed.package || '' });
        } catch (e) {}
      }
      setShowBooking(true);
    }
    // Deep-link "Book Now" from simulator pages
    const openBooking = params.get('openBooking');
    if (openBooking) {
      const pkg = params.get('package') || '';
      setPreselectedService({ service: decodeURIComponent(openBooking), package: decodeURIComponent(pkg) });
      setShowBooking(true);
      const url = new URL(window.location);
      url.searchParams.delete('openBooking');
      url.searchParams.delete('package');
      window.history.replaceState({}, '', url.pathname + url.search);
    }
    // Open category tab
    const openCat = params.get('openCategory');
    if (openCat) {
      setActivePricingCategory(openCat);
      const url = new URL(window.location);
      url.searchParams.delete('openCategory');
      window.history.replaceState({}, '', url.pathname + url.hash);
    }
  }, []);

  const handleSplashComplete = () => {
    setShowSplash(false);
    sessionStorage.setItem('pdx_intro_seen', 'true');
  };

  const handleServiceClick = (categoryId) => {
    setActivePricingCategory(categoryId);
    const el = document.querySelector('#pricing');
    if (el) {
      const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
      window.scrollTo({ top: pos, behavior: 'smooth' });
    }
  };

  const handleBookNow = (serviceInfo) => {
    setPreselectedService(serviceInfo);
    setShowBooking(true);
  };

  // Listen for close-booking event from the modal
  useEffect(() => {
    const handleClose = () => setShowBooking(false);
    window.addEventListener('close-booking', handleClose);
    return () => window.removeEventListener('close-booking', handleClose);
  }, []);

  return (
    <div className="min-h-screen" style={{ background: '#000000' }}>
      {showSplash && <IntroSplash onComplete={handleSplashComplete} />}
      <main>
        <HeroSection />
        <PricingSection
          onBookNow={handleBookNow}
          externalCategory={activePricingCategory}
          onCategoryChange={setActivePricingCategory}
        />
        {showBooking && <BookingSection preselected={preselectedService} />}
      </main>
      <Footer />
      <Toaster position="top-right" />
    </div>
  );
};

export default Home;
